import { z } from 'zod';
import { submissionFailureSchema } from '../autopilot/result.ts';
import { pendingDecisionSchema, timelineEntrySchema } from '../autopilot/status.ts';
import { actorSchema, isoDateTimeSchema, paginationQuerySchema } from './common.ts';

/**
 * A quote number OR a policy number (autopilot resolves a linked policy number to its main
 * application). Matching in autopilot is exact and case-sensitive and it only trims, so the value
 * is trimmed here and never case-folded.
 *
 * The exact format is not known, so this stays permissive: 1-40 characters, alphanumeric start,
 * then letters, digits, underscore or hyphen.
 */
export const quoteNumberSchema = z
  .string()
  .trim()
  .min(1)
  .max(40)
  .regex(/^[A-Za-z0-9][A-Za-z0-9_-]*$/, 'Use letters, digits, "_" or "-"');

/**
 * Strict object: unknown keys are rejected with VALIDATION_FAILED (zod's default `z.object`
 * would silently strip them). `options` is the extension point for autopilot input that is
 * not final yet.
 */
export const createSubmissionRequestSchema = z.strictObject({
  quoteNumber: quoteNumberSchema,
  note: z.string().trim().max(2000).optional(),
  options: z.record(z.string(), z.unknown()).optional(),
  /**
   * Set to true to run a quote again after a previous closed run that may have produced side
   * effects. Without it the API answers 409 CONFLICT (`RERUN_CONFIRMATION_REQUIRED`).
   */
  confirmRerun: z.boolean().optional(),
});
export type CreateSubmissionRequest = z.infer<typeof createSubmissionRequestSchema>;

export const requestStatusSchema = z.enum([
  'STARTING',
  'START_FAILED',
  'RUNNING',
  'AWAITING_DECISION',
  'COMPLETED',
  'FAILED',
  'CANCELLED',
  'TERMINATED',
  'TIMED_OUT',
  'UNKNOWN',
]);
export type RequestStatus = z.infer<typeof requestStatusSchema>;

/** `details.reason` of the 409 CONFLICT returned when a re-run needs explicit confirmation. */
export const RERUN_CONFIRMATION_REQUIRED = 'RERUN_CONFIRMATION_REQUIRED';

export const rerunConfirmationDetailsSchema = z.object({
  reason: z.literal(RERUN_CONFIRMATION_REQUIRED),
  previousRequestId: z.string(),
  previousStatus: requestStatusSchema,
});
export type RerunConfirmationDetails = z.infer<typeof rerunConfirmationDetailsSchema>;

export const submissionSummarySchema = z.object({
  requestId: z.string(),
  quoteNumber: z.string(),
  status: requestStatusSchema,
  stage: z.string().nullable(),
  /** The quote autopilot resolved the input to; null until the run has finished. */
  resolvedQuoteNumber: z.string().nullable(),
  policyNumber: z.string().nullable(),
  workflowId: z.string(),
  runId: z.string().nullable(),
  createdBy: actorSchema,
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
});
export type SubmissionSummary = z.infer<typeof submissionSummarySchema>;

export const submissionDetailSchema = submissionSummarySchema.extend({
  timeline: z.array(timelineEntrySchema),
  pendingDecision: pendingDecisionSchema.nullable(),
  temporalUiUrl: z.string().nullable(),
  note: z.string().nullable(),
  /** Set when the run failed. */
  failure: submissionFailureSchema.nullable(),
});
export type SubmissionDetail = z.infer<typeof submissionDetailSchema>;

/** Query strings carry 'true'/'false'; `z.coerce.boolean()` would turn 'false' into true. */
const queryBoolean = z.union([
  z.boolean(),
  z.enum(['true', 'false']).transform((v) => v === 'true'),
]);

export const listSubmissionsQuerySchema = paginationQuerySchema.extend({
  mine: queryBoolean.optional(),
  status: requestStatusSchema.optional(),
  quoteNumber: z.string().trim().min(1).max(40).optional(),
  from: isoDateTimeSchema.optional(),
  to: isoDateTimeSchema.optional(),
});
export type ListSubmissionsQuery = z.infer<typeof listSubmissionsQuerySchema>;

export const createSubmissionResponseSchema = submissionSummarySchema;
export type CreateSubmissionResponse = SubmissionSummary;
